import { UserInvitation } from '../../api/organization';
import { WorkspaceAccessResponse } from '../../api/workspace';

/**
 * A pending invitation and the membership it produced are two independent queries, so the
 * member list and the invite list can disagree: the invite query is cached for 24h and nothing
 * invalidates it when someone accepts, and even with fresh data the two fetches can straddle the
 * moment of acceptance. Whenever they disagree the membership is the truth — an invitation for
 * someone who is already in the space has, by definition, been consumed.
 *
 * Without this the invitee shows up twice in Space Management: once as a real member and once as
 * a ghost "Pending invitation" row. (#2988)
 */
export const isInviteSupersededByMembership = (
  inviteEmail: string | null | undefined,
  members: Pick<WorkspaceAccessResponse, 'User'>[]
): boolean => {
  const email = inviteEmail?.trim().toLowerCase();
  if (!email) return false;
  return members.some((member) => member.User?.email?.trim().toLowerCase() === email);
};

/** Pending invitations still worth showing for this space, i.e. those no membership has replaced. */
export const withoutConsumedInvites = (
  invites: UserInvitation[],
  members: Pick<WorkspaceAccessResponse, 'User'>[]
): UserInvitation[] => invites.filter((invite) => !isInviteSupersededByMembership(invite.email, members));
